import React, {
  useCallback,
  useState,
  useEffect,
  ChangeEventHandler,
  ReactNode,
} from 'react';
import styled from 'styled-components';
import T from 'i18n-react';

import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import OutlinedInput, {
  OutlinedInputProps,
} from '@material-ui/core/OutlinedInput';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import withStyles from '@material-ui/core/styles/withStyles';
import CreateIcon from '@material-ui/icons/Create';

import ReasonAccordion from '../components/ReasonAccordion';

type Props = Omit<OutlinedInputProps, 'inputProps' | 'onChange' | 'error'> & {
  value: string | null;
  htmlFor: string;
  minLength?: number;
  maxLength: number;
  rows: number;
  onChange?: (value: string, valid: boolean) => void;
  template?: {
    text: string;
    onClick: () => void;
  };
};

type InvalidState = keyof Omit<ValidityState, 'valid'> | null;

const MultilineTextField = ({
  label,
  htmlFor,
  required,
  value,
  minLength,
  maxLength,
  rows,
  template,
  onChange,
  ...rest
}: Props) => {
  const [invalidState, setInvalidState] = useState<InvalidState>(null);

  const handleChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (event) => {
      setInvalidState(getInvalidState(event.target.validity));
      onChange?.(event.target.value, event.target.validity.valid);
    },
    [setInvalidState, onChange],
  );

  const error = invalidState != null;
  const errorMessage = getErrorMessage(invalidState, { minLength, maxLength });

  const valueLength = value?.length || 0;
  const validLength = valueLength <= maxLength;

  useEffect(() => {
    if (error || validLength) return;

    // テンプレートボタンによる文章追加では onChange は発火しないため、文字数オーバーの判定ができない。
    // よって文字数オーバーだがエラーでない状態のときは、関連処理（state更新およびコールバック）を実行する。
    setInvalidState('tooLong');
    onChange?.(value || '', false);
  }, [error, validLength, setInvalidState, onChange, value]);

  return (
    <FormControl component="fieldset" fullWidth>
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <Label required={required} htmlFor={htmlFor} error={error}>
            {label}
          </Label>
        </Grid>
        {/* テンプレートボタン */}
        {template != null && (
          <Grid item>
            <TemplateButton
              variant="text"
              startIcon={<CreateIcon fontSize="small" />}
              size="small"
              onClick={template.onClick}
            >
              {template.text}
            </TemplateButton>
          </Grid>
        )}
      </Grid>
      {htmlFor === 'applicantMotive' && <ReasonAccordion />}
      <OutlinedInput
        fullWidth
        multiline
        required={required}
        id={htmlFor}
        value={value}
        error={error}
        inputProps={{
          minLength,
          maxLength,
          rows,
        }}
        onChange={handleChange}
        {...rest}
      />
      <HelperText error={error}>
        {error && <span>{errorMessage}</span>}
        {/* 文字数カウント */}
        <Counter>
          {valueLength} / {maxLength}
        </Counter>
      </HelperText>
    </FormControl>
  );
};

export default MultilineTextField;

const Label = withStyles((theme) => ({
  root: {
    color: theme.palette.text.primary,
  },
}))(FormLabel);

const TemplateButton = withStyles({
  root: {
    alignItems: 'flex-start',
    color: '#4F46E5',
  },
})(Button);

const HelperText = withStyles({
  root: {
    display: 'inline-flex',
    flexWrap: 'nowrap',
  },
})(FormHelperText);

const Counter = styled.span`
  margin-left: auto;
  padding-left: 4px;
  white-space: nowrap;
`;

const getInvalidState = (validityState: ValidityState): InvalidState => {
  // Object.keys などでキーを取得できないので、全ての分岐入れている
  if (validityState.tooLong) return 'tooLong';
  if (validityState.tooShort) return 'tooShort';
  if (validityState.valueMissing) return 'valueMissing';
  if (validityState.badInput) return 'badInput';
  if (validityState.customError) return 'customError';
  if (validityState.patternMismatch) return 'patternMismatch';
  if (validityState.rangeUnderflow) return 'rangeUnderflow';
  if (validityState.rangeOverflow) return 'rangeOverflow';
  if (validityState.stepMismatch) return 'stepMismatch';
  if (validityState.typeMismatch) return 'typeMismatch';

  return null;
};

const getErrorMessage = (
  invalidState: InvalidState,
  { minLength, maxLength },
): ReactNode => {
  switch (invalidState) {
    case 'tooLong':
      // 文字数オーバー
      return T.translate('form.tooLong', { length: maxLength });
    case 'tooShort':
      // 文字数不足
      return T.translate('form.tooShort', { length: minLength });
    case 'valueMissing':
      // 未入力
      return T.translate('form.required');
    default:
      // 不正な入力値（デフォルト）
      return T.translate('form.invalid');
  }
};
